import {getMimeTypeFromFilename} from '@shopify/mime-types';
import {Method} from '@shopify/network';
import {useMutation} from '@shopify/react-graphql';
import {StagedUploadHttpMethodType} from 'types/graphql/default-types';
import {
  ImageDefaults,
  MediaValidationStatus,
  validateImage,
} from 'utilities/validate-image';

import {type MediaUploaderProps} from '../../MediaUploader';

import UploadStagedMediaMutation from './graphql/UploadStagedMediaMutation.graphql';
import type {
  Result,
  MediasUploadResults,
  StageMediasResult,
  ResultSuccess,
  ResultFinalUploadFailed,
  ResultStagedUploadFailed,
  StagedUploadResult,
  ResultValidationError,
} from './types';
import {Status} from './types';
import {
  getMediaUserErrorMessage,
  getPath,
  getStagedUploadTargetGenerateUploadResourceType,
} from './utilities';

export function normalizeStringForAWSKey(name: string) {
  return name.replace(/[\s]/gi, '');
}

export const useMediaUploader = ({
  resourceType,
  onMediaUploaded,
  onAllMediasUploaded,
  validate,
}: MediaUploaderProps) => {
  const uploadStagedMediaMutation = useMutation(UploadStagedMediaMutation);

  const uploadMedias = async (medias: File[]): Promise<Result[]> => {
    const {validMedias, errors: validationErrors} =
      await validateMedias(medias);

    const {stagedMedias, errors: stagingErrors} =
      await stageMedias(validMedias);

    const {uploadedMedias, errors: uploadErrors} =
      await finalUploadMedias(stagedMedias);

    return ([] as Result[]).concat(
      validationErrors as Result[],
      stagingErrors as Result[],
      uploadErrors as Result[],
      uploadedMedias as Result[],
    );
  };

  const validateMedias = async (
    medias: File[],
  ): Promise<{
    errors: ResultValidationError[];
    validMedias: File[];
  }> => {
    if (validate) {
      const validationResults = await Promise.all(
        medias.map(async (media) => {
          const validation = await validateImage(media, {
            maxFileSize: ImageDefaults.maxFileSizeInBytes,
            maxResolutionInMegapixels: ImageDefaults.maxMegapixels,
            validMimeTypes: ImageDefaults.validMimeTypes,
          });

          if (validation.status === MediaValidationStatus.Valid) {
            return media;
          } else {
            const resultValidationError = {
              media,
              status: Status.ValidationFailed,
              error: validation,
            } as ResultValidationError;

            if (onMediaUploaded) {
              onMediaUploaded(resultValidationError);
            }
            return resultValidationError;
          }
        }),
      );

      return {
        errors: validationResults.filter(
          (
            result: ResultValidationError | File,
          ): result is ResultValidationError => {
            return (result as ResultValidationError).media !== undefined;
          },
        ),
        validMedias: validationResults.filter(
          (result: ResultValidationError | File): result is File => {
            return (result as ResultValidationError).media === undefined;
          },
        ),
      };
    } else {
      return {
        errors: [],
        validMedias: medias,
      };
    }
  };

  const stageMedias = async (allMedia: File[]): Promise<StageMediasResult> => {
    if (allMedia.length === 0) {
      return {
        errors: [],
        stagedMedias: [],
      };
    }
    const mediasUploadResults: MediasUploadResults = {
      hasReturnedNull: false,
      urls: [],
      userErrors: [],
    };

    let uploadMediaFailed = false;

    try {
      const input = allMedia.map((media) => {
        const resource = resourceType
          ? resourceType
          : getStagedUploadTargetGenerateUploadResourceType(media);
        return {
          filename: normalizeStringForAWSKey(media.name),
          mimeType: getMimeTypeFromFilename(media.name) || media.type || '',
          resource,
          fileSize: media.size.toString(),
          httpMethod: StagedUploadHttpMethodType.Post,
        };
      });

      const {data} = await uploadStagedMediaMutation({variables: {input}});
      const stagedUploadsCreate = data?.stagedUploadsCreate;

      if (stagedUploadsCreate == null) {
        mediasUploadResults.hasReturnedNull = true;
      } else {
        if (stagedUploadsCreate.stagedTargets) {
          mediasUploadResults.urls = stagedUploadsCreate.stagedTargets
            .filter((target) => target.url !== undefined)
            .map((target) => ({
              url: target.url as string,
              resourceUrl: target.resourceUrl,
              parameters: target.parameters,
            }));
        }
        if (stagedUploadsCreate.userErrors) {
          mediasUploadResults.userErrors = stagedUploadsCreate.userErrors;
        }
      }
    } catch {
      uploadMediaFailed = true;
    }

    if (
      uploadMediaFailed ||
      mediasUploadResults.hasReturnedNull ||
      mediasUploadResults.userErrors.length > 0
    ) {
      const stageUploadErrorMedias = allMedia.map((media, index) => {
        return {
          media,
          status: Status.StagedUploadFailed,
          message: getMediaUserErrorMessage(mediasUploadResults, index),
        } as ResultStagedUploadFailed;
      });

      if (onMediaUploaded) {
        stageUploadErrorMedias.forEach((image) => onMediaUploaded(image));
      }

      return {
        errors: stageUploadErrorMedias,
        stagedMedias: [],
      };
    }

    return mediasUploadResults.urls.reduce<StageMediasResult>(
      (acc, stagedUploadResult, index) => {
        if (stagedUploadResult.url === null) {
          const resultStagedUploadFailed: ResultStagedUploadFailed = {
            media: allMedia[index],
            status: Status.StagedUploadFailed,
          };
          if (onMediaUploaded) {
            onMediaUploaded(resultStagedUploadFailed);
          }
          acc.errors.push(resultStagedUploadFailed);
        } else {
          acc.stagedMedias.push({
            stagedUploadResult,
            media: allMedia[index],
          });
        }

        return acc;
      },
      {errors: [], stagedMedias: []},
    );
  };

  const finalUploadMedias = async (
    stagedMedias: {
      stagedUploadResult: StagedUploadResult;
      media: File;
    }[],
  ): Promise<{
    errors: ResultFinalUploadFailed[];
    uploadedMedias: ResultSuccess[];
  }> => {
    const finalUploadResults = await Promise.all(
      stagedMedias.map(async ({stagedUploadResult, media}) => {
        try {
          const finalUploadErrorMedia: ResultFinalUploadFailed = {
            media,
            status: Status.FinalUploadFailed,
          };

          if (!resourceType && !stagedUploadResult.resourceUrl) {
            return finalUploadErrorMedia;
          }

          const formData = new FormData();
          stagedUploadResult.parameters.forEach(({name, value}) => {
            formData.append(name, value);
          });

          const mediaName = normalizeStringForAWSKey(media.name);
          formData.append('file', media, mediaName);

          const response = await fetch(stagedUploadResult.url, {
            method: Method.Post,
            body: formData,
          });

          if (response.ok) {
            const path = await getPath(
              response,
              stagedUploadResult,
              resourceType,
            );

            const result: ResultSuccess = {
              media,
              status: Status.Success,
              path,
            };
            if (onMediaUploaded) {
              onMediaUploaded(result);
            }
            return result;
          }
          if (onMediaUploaded) {
            onMediaUploaded(finalUploadErrorMedia);
          }

          return finalUploadErrorMedia;
        } catch (error) {
          const finalUploadErrorMedia: ResultFinalUploadFailed = {
            media,
            status: Status.FinalUploadFailed,
          };
          if (onMediaUploaded) {
            onMediaUploaded(finalUploadErrorMedia);
          }

          return finalUploadErrorMedia;
        }
      }),
    );

    return {
      errors: finalUploadResults.filter(
        (
          result: ResultFinalUploadFailed | ResultSuccess,
        ): result is ResultFinalUploadFailed => {
          return result.status === Status.FinalUploadFailed;
        },
      ),
      uploadedMedias: finalUploadResults.filter(
        (
          result: ResultFinalUploadFailed | ResultSuccess,
        ): result is ResultSuccess => {
          return result.status === Status.Success;
        },
      ),
    };
  };

  const uploadMedia = async (media: File[]) => {
    const results = await uploadMedias(media);

    if (onAllMediasUploaded) {
      await onAllMediasUploaded(results);
    }
  };

  return {uploadMedia};
};
